import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Sparkles, Utensils, Users, Coffee, UtensilsCrossed, ChefHat, Soup } from 'lucide-react';
import { Button } from '../components/ui/button';
import { images } from '../data/mock';

const TarinaPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-stone-800 via-stone-900 to-stone-800 overflow-hidden">
        <div className="absolute top-10 right-10 w-32 h-32 border-4 border-emerald-500/20 rounded-full" />
        <div className="absolute bottom-20 left-20 w-48 h-48 border-4 border-emerald-500/10 rounded-full" />
        
        {/* Background image */}
        <div className="absolute inset-0 opacity-20">
          <img
            src={images.restaurant}
            alt="Ravintola Data"
            className="w-full h-full object-cover"
          />
        </div>
        
        {/* Logo watermark */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img
            src={images.logo}
            alt=""
            className="w-64 md:w-80 lg:w-96 opacity-10"
          />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="font-handwritten text-4xl text-emerald-400 block mb-2">Keitä me olemme?</span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Tarinamme
          </h1>
          <p className="text-lg text-stone-300 max-w-2xl mx-auto">
            Mikä tekee paikasta enemmän kuin ravintolan? 
            <span className="font-handwritten text-xl text-emerald-400"> Ihmiset, intohimo ja rehellinen tekeminen.</span>
          </p>
        </div>
      </section>

      {/* Main Story - Engaging */}
      <section className="py-20 md:py-28 bg-white relative overflow-hidden">
        <div className="absolute top-20 right-10 w-24 h-24 bg-emerald-100 rounded-full opacity-50" />
        <div className="absolute bottom-40 left-10 w-32 h-32 bg-amber-100 rounded-full opacity-40" />
        
        <div className="max-w-4xl mx-auto px-6 relative">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-red-400 to-rose-500 rounded-full mb-6 shadow-lg animate-pulse">
              <Heart className="w-10 h-10 text-white" />
            </div>
          </div>

          <h2 className="text-4xl md:text-5xl font-bold text-stone-800 mb-8 text-center">
            Ruoka on <span className="font-handwritten text-emerald-600">tärkein asia</span>
          </h2>
          
          <div className="space-y-6 text-stone-600 leading-relaxed text-lg">
            <p className="bg-emerald-50 p-6 rounded-2xl border-2 border-emerald-100 transform -rotate-[0.3deg]">
              Ravintola Data syntyi yksinkertaisesta ajatuksesta: <strong className="text-emerald-600">tehdään ruokaa, joka maistuu hyvältä.</strong> Ei temppuja, ei turhia koristeluja – vain selkeitä makuja ja huolella tehtyä ruokaa.
            </p>
            
            <p>
              Keittiössä päivä alkaa aikaisin. Uunit lämpenevät, veitsi osuu leikkuulautaan ja kahvin tuoksu sekoittuu keiton höyryyn.
              <span className="font-handwritten text-2xl text-emerald-600"> Kaikki tehdään täällä, tänään, omin käsin.</span>
            </p>

            <div className="bg-gradient-to-r from-amber-100 to-emerald-100 p-8 rounded-3xl my-10 border-l-4 border-emerald-400 transform rotate-[0.5deg] hover:rotate-0 transition-transform">
              <p className="text-stone-700 text-xl leading-relaxed m-0">
                Meille hyvä ruoka on sellaista, jota syödessä ei tarvitse miettiä.
                <span className="font-handwritten text-2xl text-amber-700 block mt-2"> Se maistuu tutulta, täyttää vatsan ja jättää hyvän olon.</span>
              </p>
              <Heart className="w-6 h-6 text-red-400 mt-4" />
            </div>

            <p className="bg-stone-50 p-6 rounded-2xl border-2 border-stone-100 transform rotate-[0.3deg]">
              Emme yritä olla jotain, mitä emme ole. Olemme kotkalainen lounaspaikka, jossa <span className="bg-emerald-100 px-2 py-1 rounded">ruoka on rehellistä</span> ja vastaanotto lämmin.
            </p>
          </div>
        </div>
      </section>

      {/* Values Section - Playful icons */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-stone-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-emerald-200 rounded-3xl rotate-2" />
              <img
                src={images.saladBuffet}
                alt="Tuoreet raaka-aineet"
                className="relative rounded-2xl shadow-xl w-full border-4 border-white"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl shadow-xl p-4 transform rotate-3 hover:rotate-0 transition-transform">
                <span className="font-handwritten text-3xl text-emerald-600">Aitoa!</span>
              </div>
            </div>
            <div>
              <span className="font-handwritten text-3xl text-emerald-500">Arvomme</span>
              <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-8 mt-2">
                Mihin uskomme
              </h2>
              <div className="space-y-6">
                <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-emerald-100 transform -rotate-[0.5deg] hover:rotate-0 transition-transform">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-md">
                      <Utensils className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-stone-800 text-lg">Tuoreus ei ole slogan</h3>
                  </div>
                  <p className="text-stone-600 leading-relaxed">
                    Meille tuoreus ei ole mainoslause. Se tarkoittaa sitä, että keitot keitetään, salaatit pilkotaan ja pullat leivotaan samana päivänä, kun ne syödään.
                  </p>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-amber-100 transform rotate-[0.5deg] hover:rotate-0 transition-transform">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-yellow-500 rounded-xl flex items-center justify-center shadow-md">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-stone-800 text-lg">Yksinkertaisuus on voimaa</h3>
                  </div>
                  <p className="text-stone-600 leading-relaxed">
                    Hyvä ruoka ei tarvitse kikkailua. Se tarvitsee hyvät raaka-aineet, rauhallisen tekemisen ja oikean asenteen.
                  </p>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-lg border-2 border-rose-100 transform -rotate-[0.3deg] hover:rotate-0 transition-transform">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center shadow-md">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-semibold text-stone-800 text-lg">Ihminen edellä</h3>
                  </div>
                  <p className="text-stone-600 leading-relaxed">
                    Meillä ruoka tehdään ihmisille – ja ihmisiä varten. <span className="font-handwritten text-xl text-rose-600">Jokainen asiakas on tervetullut sellaisena kuin on.</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section - Big and bold */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-emerald-500 to-emerald-500 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <Soup className="w-12 h-12 text-emerald-200 mx-auto mb-6" />
          <blockquote className="text-2xl md:text-4xl text-white font-medium leading-relaxed">
            "Ruoka on meidän tapamme sanoa: 
            <span className="font-handwritten text-3xl md:text-5xl block mt-4 text-emerald-100">tervetuloa, istu alas, nauti hetkestä.</span>"
          </blockquote>
          <p className="mt-8 text-emerald-200 flex items-center justify-center gap-2">
            <Heart className="w-5 h-5" />
            Ravintola Datan keittiö
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="font-handwritten text-3xl text-emerald-500 block mb-4">Tervetuloa!</span>
          <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">
            Tule käymään
          </h2>
          <p className="text-lg text-stone-600 mb-8">
            Paras tapa tutustua on tulla paikan päälle. 
            <span className="font-handwritten text-xl text-emerald-600"> Odotamme sinua!</span>
          </p>
          <Link to="/yhteystiedot">
            <Button className="bg-stone-900 hover:bg-stone-800 text-white px-8 py-4 text-lg rounded-2xl shadow-xl hover:shadow-2xl transition-all group">
              Löydä perille
              <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TarinaPage;
